// Pure helpers over TimelineEntry ids. Zero React imports — trivially
// unit-testable next to the applyEvent tests.

/**
 * Numeric tail of a bus-event entry id (`${sessionId}#${seq}`, see `eventId`
 * in useWsSession). The seq is bus-monotonic and stable across a reconnect
 * replay, so within one session it is a sound total order for "which entry is
 * newer" — array index is NOT (readiness cards are replaced in place, keeping
 * an old index under a new id). Callers must only feed bus-event ids: the
 * client-synthesized `proto#N` / `unknown_intent#N` ids share the `#` shape
 * but their tails count local errors, not bus seqs.
 */
export const entrySeq = (id: string): number =>
  Number(id.slice(id.lastIndexOf("#") + 1));

/**
 * Numeric bus seq of an `agent_ask` entry id, whose shape is
 * `${sessionId}#${seq}#${askId}` (see the `question_pending` fold in
 * useWsSession). {@link entrySeq} reads the tail and would parse the ASK ID
 * here — `Number("ask-1")` is NaN, and NaN poisons every `<=` comparison it
 * touches, so a "newest wins" fold silently becomes "any NaN wins, then
 * everything after it".
 */
export const askEntrySeq = (id: string): number => {
  const first = id.indexOf("#");
  if (first === -1) return Number.NaN;
  const rest = id.slice(first + 1);
  const second = rest.indexOf("#");
  return Number(second === -1 ? rest : rest.slice(0, second));
};
